/**
 * Copy a string, and say so.
 *
 * The confirmation is the point. Without it there is no way to tell a
 * successful copy from a click that did nothing, and the natural response to
 * that doubt is to press again — which is harmless here but teaches the person
 * not to trust the button.
 *
 * Copying can genuinely fail: `navigator.clipboard` is unavailable on an
 * insecure origin and can be refused by permission policy. That is reported
 * rather than swallowed, because the caller can still select the text by hand
 * once they know the button did not work.
 */
import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button, type ButtonSize, type ButtonVariant } from "./Button";
import { useToast } from "./Toast";

export function CopyButton({
  value,
  label = "Copy",
  copiedLabel = "Copied",
  size = "sm",
  variant,
  disabled,
  title,
}: {
  value: string;
  label?: string;
  copiedLabel?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  disabled?: boolean;
  title?: string;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      toast({
        tone: "warning",
        title: "Could not copy",
        description: "Your browser blocked it — select the link and copy it by hand.",
      });
    }
  };

  return (
    <Button
      size={size}
      variant={variant}
      title={title}
      icon={copied ? <Check /> : <Copy />}
      disabled={disabled || value === ""}
      onClick={() => void copy()}
    >
      {copied ? copiedLabel : label}
    </Button>
  );
}
